import React, { useEffect, useRef } from "react";

export default function SlideOverPanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  testId,
  zIndexClass = "z-50",
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  testId?: string;
  zIndexClass?: string;
}) {
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 flex justify-end bg-black/30 ${zIndexClass}`}
      onMouseDown={(e) => {
        pressStartedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const startedOnBackdrop = pressStartedOnBackdrop.current;
        pressStartedOnBackdrop.current = false;
        if (startedOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      data-testid={testId}
    >
      <aside className="flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-snow-primary">{title}</h2>
            {subtitle ? (
              <div className="mt-1 flex items-center gap-2">{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            autoFocus
            className="text-slate-500 hover:text-slate-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {footer ? (
          <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
            {footer}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
